import axios from 'axios';
// import { useNavigate } from 'react-router-dom'; // For navigation to login page
import { BASE_URL } from "./baseURL"

const instance = axios.create({
   baseURL: BASE_URL,
  withCredentials: true, // Ensure cookies are sent with requests
});

const REFRESH_URL = '/api/v1/refresh';

// These "am I logged in?" checks are used by the public/protected route
// guards to decide what to render. A 401 from them is a normal, expected
// state (visitor simply isn't logged in yet) - NOT a session expiry, so it
// must not trigger a refresh attempt or a forced redirect to /login.
const SILENT_AUTH_CHECK_URLS = [
  '/api/v1/auth/isLoggedin',
  '/api/v1/auth/isAdmin',
  '/api/v1/auth/isSuperAdmin',
];

// Shared in-flight refresh promise so multiple requests that 401 at the same
// time (e.g. several widgets/guards on one page load) trigger a single
// /refresh call instead of one each. Uses plain axios (not `instance`) so a
// failed refresh doesn't re-enter this same interceptor recursively.
let refreshPromise = null;

const getRefreshPromise = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${BASE_URL}${REFRESH_URL}`, {}, { withCredentials: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Add a response interceptor to handle token expiration
instance.interceptors.response.use(
  (response) => response, // Pass through the response if it's successful
  async (error) => {
    const originalRequest = error.config;

    const isSilentAuthCheck = SILENT_AUTH_CHECK_URLS.some((url) =>
      originalRequest?.url?.includes(url)
    );
    const shouldAttemptRefresh =
      error.response &&
      error.response.status === 401 &&
      originalRequest &&
      !originalRequest._retry;

    if (shouldAttemptRefresh) {
      originalRequest._retry = true;
      try {
        await getRefreshPromise();
        return instance(originalRequest); // Retry the original request once
      } catch (refreshError) {
        // A silent auth check (isLoggedin/isAdmin/isSuperAdmin) failing is a
        // normal "not logged in" state - the calling route guard already
        // handles it. Only force-redirect for real data requests, so we
        // don't yank the user off /adminlogin, /superadminlogin, etc.
        if (!isSilentAuthCheck) {
          navigateToLogin();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Function to handle redirection to the login page
function navigateToLogin() {
  // Avoid a reload loop: if we're already on /login, another redirect just
  // remounts the app and re-triggers the same failed auth check forever.
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

export default instance;
